'use client';

import { Box, Stack, Theme } from '@mui/material';
import { ReactElement } from 'react';

/**
 * The tone a *filled* step carries.
 *
 * Named by tone rather than by level on purpose. `Substantial` exists only on the document axis, so
 * a level-named token would leak one axis's vocabulary into a palette the other axis also reads
 * from. Tones are the vocabulary both axes share.
 */
export type TConfidenceTone = 'none' | 'pending' | 'low' | 'substantial' | 'high';

export type TSegmentedMeterProps = {
  /** How many steps the ladder has in total. Always rendered, filled or not. */
  bars: number;
  /** How many of those steps are filled. Clamped into range. */
  filled: number;
  /**
   * The colour the filled steps take. Has no effect at `filled={0}`, where every step renders in the
   * `none` tone regardless of what is passed here.
   *
   * Independent of `filled` by design: the biometric axis fills one step in `pending` for a check
   * that returned nothing yet, and one step in `low` for a check that returned a poor result. Same
   * count, different meaning, so the count cannot imply the colour.
   */
  tone: TConfidenceTone;
  /** Defaults to the 8rem the design uses on a check card. */
  maxWidth?: string | number;
  /**
   * Names what the meter is measuring, for assistive technology.
   *
   * **Omit it and the meter becomes decorative.** In both placements the design gives it, the level
   * is already stated in text immediately beside the bars ("Substantial confidence"), so announcing
   * it again just makes a screen reader say the same thing twice. Passing a label opts into the
   * `meter` role for a placement where no such text exists.
   */
  'aria-label'?: string;
};

/**
 * The dumb bar meter underneath every confidence display: N steps, M of them filled, in one tone.
 *
 * **Deliberately knows nothing about confidence levels.** It is the primitive tier, so it takes
 * counts and a tone and nothing else. The semantic tier above it (`DocumentConfidenceMeter`,
 * `BiometricConfidenceMeter`) owns the mapping from a level to these numbers, which is why this is
 * not exported from the package barrel: a call site reaching for it directly would be re-deriving
 * that mapping, and re-derivation is exactly what the semantic tier exists to prevent.
 *
 * **Takes no `data-testid`, deliberately.** A `data-testid` has to be unique on the page (ADR 72),
 * and this primitive appears several times on one, nested inside components that carry their own.
 * Exposing the prop would hand that uniqueness obligation to every call site with nothing checking
 * the result. Locate it from the ancestor that does carry one, or by its `meter` role when named.
 */
export default function SegmentedMeter({
  bars,
  filled,
  tone,
  maxWidth,
  'aria-label': ariaLabel,
}: Readonly<TSegmentedMeterProps>): ReactElement | null {
  // A ladder with no steps is not a ladder. Render nothing rather than an empty flex row that still
  // takes its gap and reads on screen as a hairline.
  if (!Number.isFinite(bars) || bars < 1) {
    return null;
  }

  const stepCount = Math.floor(bars);
  const filledCount = Math.min(Math.max(Math.floor(filled) || 0, 0), stepCount);

  // An unnamed `meter` announces as an unnamed widget, which is worse than staying silent next to
  // text that already says the level. So the role is opt-in, and the default is decorative.
  const semantics = ariaLabel
    ? {
        role: 'meter',
        'aria-label': ariaLabel,
        'aria-valuenow': filledCount,
        'aria-valuemin': 0,
        'aria-valuemax': stepCount,
      }
    : { 'aria-hidden': true };

  return (
    <Stack
      direction="row"
      {...semantics}
      sx={(t: Theme) => ({
        gap: t.semantic.spacing['3xs'],
        width: '100%',
        maxWidth: maxWidth ?? t.semantic.size['3xs'],
      })}
    >
      {Array.from({ length: stepCount }, (_, index) => {
        const isFilled = index < filledCount;
        return (
          <Box
            key={index}
            data-filled={isFilled}
            sx={(t: Theme) => ({
              flex: '1 1 0%',
              height: t.semantic.spacing['2xs'],
              backgroundColor: isFilled
                ? t.palette.confidence[tone].fill
                : t.palette.confidence.none.fill,
            })}
          />
        );
      })}
    </Stack>
  );
}
